import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

/**
 * A custom hook to extract specific query parameters from the URL.
 * It returns an object with the requested parameter keys and their corresponding values.
 *
 * @example
 * const { userId, token } = useQueryParams('userId', 'token');
 *
 * @param {...string} keys The names of the query parameters to extract.
 * @returns {Object.<string, string|null>} An object where keys are the parameter names
 *   and values are their corresponding string values from the URL, or `null` if not present.
 */
export function useQueryParams(...keys) {
  const { search } = useLocation();

  // The linter warns against spreading `keys` in the dependency array because it
  // cannot be statically verified. We create a stable, primitive dependency
  // by joining the keys into a single string.
  const joinedKeys = keys.join();

  return useMemo(() => {
    const params = new URLSearchParams(search);
    const queryValues = {};
    for (const key of keys) {
      queryValues[key] = params.get(key);
    }
    return queryValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, joinedKeys]);
}
